import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { AppMark } from './Icons.js'
import { loadSeat } from '../net/session.js'
import { reconnect } from '../net/socket.js'
import type { ConnectionState } from '../net/useGame.js'

/**
 * Continuous milliseconds offline before the screen says so. A phone changing
 * cells or a laptop waking up drops the socket for a beat and gets it straight
 * back; announcing that instantly means the first thing a player sees on a
 * perfectly healthy table is "cannot reach the server".
 */
const OFFLINE_GRACE_MS = 2000

/**
 * Connected but empty-handed for this long and we admit something is wrong.
 * The server sends the view the moment it accepts a resume, so this should
 * never fire - which is exactly why it is here. It is the only remaining way
 * to sit on this screen forever, and a spinner with no explanation is the
 * failure this whole component exists to stop.
 */
const SILENCE_MS = 8000

export interface ConnectingProps {
  /** The lobby code, already upper-cased. */
  code: string
  /** What the socket is currently doing. */
  status: ConnectionState
  /**
   * A phone or the room's screen. It sets the scale - a table screen is read
   * from across a room and a phone from a hand - and whether the seat
   * reassurance is relevant at all, because a table screen holds no seat.
   */
  variant?: 'phone' | 'board'
}

/**
 * The screen between opening a lobby link and the game appearing.
 *
 * It used to be one line of 14px grey text in the top-left corner of an
 * otherwise empty page, which is what an app looks like when it has crashed.
 * The three things it now does, in order of how much they matter:
 *
 * - It moves. Same argument as `WaitingOn`: a static line is indistinguishable
 *   from a frozen app, and the pulse is the app saying it is still trying.
 * - It says which lobby. The code is the only thing on this screen that
 *   identifies anything, so it is the headline rather than a fragment of a
 *   sentence.
 * - It stops lying by omission. A server that cannot be reached used to look
 *   exactly like a server that was about to answer, and went on looking like
 *   it forever. After a beat this one says what is actually happening and
 *   offers the two things worth doing about it.
 *
 * Going home from here deliberately does not hand the seat back. The socket is
 * down, so the server could not be told anyway, and the token in storage is
 * what lets somebody walk back into the same seat once the network returns -
 * dropping it here would turn a bad minute into a lost game.
 */
export function Connecting({ code, status, variant = 'phone' }: ConnectingProps) {
  /*
   * Bumped by "try again", and a dependency of both timers below, so a retry
   * puts the screen back to its calm state and then re-escalates on its own if
   * the next attempt fails too. Without it the message would sit there
   * unchanged while the app was busy doing the thing it had just asked for.
   */
  const [attempt, setAttempt] = useState(0)
  const [offlineTooLong, setOfflineTooLong] = useState(false)
  const [silentTooLong, setSilentTooLong] = useState(false)

  useEffect(() => {
    if (status !== 'offline') {
      setOfflineTooLong(false)
      return
    }
    const timer = setTimeout(() => setOfflineTooLong(true), OFFLINE_GRACE_MS)
    return () => clearTimeout(timer)
  }, [status, attempt])

  useEffect(() => {
    setSilentTooLong(false)
    const timer = setTimeout(() => setSilentTooLong(true), SILENCE_MS)
    return () => clearTimeout(timer)
  }, [attempt])

  /*
   * Read once from storage rather than watched: nothing can save a seat while
   * this screen is up, because saving one takes the server.
   */
  const seated = useMemo(() => variant === 'phone' && loadSeat(code) !== null, [code, variant])

  const retry = useCallback(() => {
    reconnect()
    setAttempt((n) => n + 1)
  }, [])

  const stalled = offlineTooLong || silentTooLong

  return (
    <div className={`connecting connecting--${variant}`} data-testid="connecting">
      <span className="connecting__pulse" aria-hidden="true">
        <span className="connecting__ring" />
        <span className="connecting__ring" />
        <span className="connecting__core">
          <AppMark />
        </span>
      </span>

      <p className="connecting__kicker">Lobby</p>
      <p className="connecting__code">{code}</p>

      {/*
        One live region covering both states rather than one each, so the
        change from "connecting" to "cannot reach the server" is announced as
        the same thing being updated rather than as a second thing arriving.
      */}
      <p className="connecting__line" role="status">
        {stalled ? (
          offlineTooLong ? (
            'Can’t reach the server — still trying.'
          ) : (
            'Connected, but the lobby hasn’t answered.'
          )
        ) : (
          <>
            Connecting
            <span className="connecting__dots" aria-hidden="true">
              <i />
              <i />
              <i />
            </span>
          </>
        )}
      </p>

      {stalled && (
        <div className="connecting__stall" data-testid="connecting-stalled">
          {seated && <p className="connecting__note">Your seat is saved on this device.</p>}
          <button
            type="button"
            className="btn btn--primary"
            onClick={retry}
            data-testid="connecting-retry"
          >
            Try again
          </button>
          {/*
            A link, and not the leave button: see the note above the component.
            The seat stays exactly where it is.
          */}
          <Link className="connecting__home" to="/" data-testid="connecting-home">
            back to the main screen
          </Link>
        </div>
      )}
    </div>
  )
}
